import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDrawer } from "@angular/material/sidenav";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DialogService, DocumentService } from "src/app/core";
import { OnboardingService, OnboardingWorkflowStepTypes } from "src/app/features/onboarding";
import {
  VisionboardCategoriesService,
  VisionboardDragDropService,
  VisionboardFavoritesDialogService,
  VisionboardService,
  VisionboardWorkspaceItemsStoreService,
} from "../../services";
import { VB_GLOBAL_SEARCH_DIALOG_ID } from "../../shared";

@Component({
    selector: "app-visionboard-workspace-freeform-container",
    templateUrl: "./visionboard-workspace-freeform-container.component.html",
    styleUrls: ["./visionboard-workspace-freeform-container.component.scss"],
    standalone: false
})
export class VisionboardWorkspaceFreeformContainerComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void>;

  private openOnDrop: boolean;
  private hasCategory: boolean;

  hideItems: boolean;
  loading: boolean;
  showVenueItems: boolean;

  @ViewChild("itemDrawer") itemDrawer: MatDrawer;

  @HostListener("window:blur")
  onBlur(): void {
    this.documentService.raiseMouseUp();
  }

  constructor(
    private visionboardService: VisionboardService,
    private visionboardCategoriesService: VisionboardCategoriesService,
    private visionboardDragDropService: VisionboardDragDropService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private documentService: DocumentService,
    private onboardingService: OnboardingService,
    private _visionboardFavoritesDialogService: VisionboardFavoritesDialogService,
    private _dialogService: DialogService,
  ) {
    this.destroyed$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.loading = true;

    this.visionboardService.mode$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        if (this.itemDrawer) {
          this.itemDrawer.close();
        }
      },
    });

    this.visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: update => {
        this.loading = !update.store.failed && !update.store.userItems;
        this.showVenueItems = update.store.venueItems && update.store.venueItems.length > 0;

        this.visionboardService.setLoading(this.loading);

        if (!this.loading) {
          this.onboardingService.trigger(OnboardingWorkflowStepTypes.VbIntro);
        }
      },
    });

    this.visionboardCategoriesService.selectedCategory$.pipe(takeUntil(this.destroyed$)).subscribe(category => {
      this.hasCategory = !!category;

      /**
       * If adding an item from the favorites modal, we will handle it there in order to deal with the "compatible items"
       * scenario.  We want to stop the expansion of the drawer if the item was added via the favorites dialog.
       *
       * When added from global search, this is also suppressing the free form slide out.
       */
      if (this._visionboardFavoritesDialogService.isOpen() || this._dialogService.isDialogOpen(VB_GLOBAL_SEARCH_DIALOG_ID)) {
        return;
      }

      if (this.itemDrawer) {
        if (this.itemDrawer.opened) {
          this.hideItems = true;
          this.itemDrawer.close();
          setTimeout(() => {
            this.hideItems = false;

            if (this.itemDrawer) {
              this.itemDrawer.toggle(this.hasCategory);
            }
          }, 400);
        } else {
          this.hideItems = false;
          this.itemDrawer.toggle(this.hasCategory);
        }
      }
    });

    this.visionboardDragDropService.dragged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.itemDrawer && this.itemDrawer.opened) {
        this.itemDrawer.close();
        this.openOnDrop = true;
      }
    });

    this.visionboardDragDropService.dropped$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      if (this.itemDrawer && this.openOnDrop) {
        this.itemDrawer.open();
        this.openOnDrop = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.visionboardDragDropService.resetState();
  }
}
